/**
 * Simplified BaseService for Supabase operations
 * Works with existing hook patterns to avoid TypeScript conflicts
 */
import { supabase } from '@/integrations/supabase/client';

export interface ServiceResponse<T> {
  data: T | null;
  error: string | null;
  success: boolean;
}

export interface ServiceListResponse<T> {
  data: T[];
  error: string | null;
  success: boolean;
  count?: number;
}

/**
 * Simple service utilities for common operations
 * Use existing hooks for complex operations
 */
export class ServiceUtils {
  /**
   * Standardized error handling
   */
  static handleError(error: any, operation: string): string {
    const errorMessage = `${operation} failed: ${error?.message || 'Unknown error'}`;
    console.error(`[ServiceUtils] ${errorMessage}`, error);
    return errorMessage;
  }

  /**
   * Standardized success logging
   */
  static logSuccess(operation: string, data?: any): void {
    console.log(`[ServiceUtils] ${operation} successful`, data);
  }

  /**
   * Create standardized success response
   */
  static createSuccessResponse<T>(data: T): ServiceResponse<T> {
    return {
      data,
      error: null,
      success: true,
    };
  }

  /**
   * Create standardized error response
   */
  static createErrorResponse<T>(error: string): ServiceResponse<T> {
    return {
      data: null,
      error,
      success: false,
    };
  }

  /**
   * Create standardized list success response
   */
  static createListSuccessResponse<T>(data: T[], count?: number): ServiceListResponse<T> {
    return {
      data,
      error: null,
      success: true,
      count,
    };
  }

  /**
   * Create standardized list error response
   */
  static createListErrorResponse<T>(error: string): ServiceListResponse<T> {
    return {
      data: [],
      error,
      success: false,
    };
  }
}